import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useUpdateProfile } from '../hooks/useProfile';
import { useChangePassword } from '../hooks/usePassword';
import { useNotification } from '@/shared/hooks/useNotification';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  {
    label: 'One special character (!@#$%^&*)',
    test: (p: string) => /[!@#$%^&*(),.?"':{}|<>]/.test(p),
  },
];

function PasswordRequirements({ password }: { password: string }) {
  if (!password) return null;
  return (
    <ul className="mt-1.5 space-y-1">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        return (
          <li
            key={rule.label}
            className={`flex items-center gap-1.5 text-xs ${met ? 'text-green-600' : 'text-muted-foreground'}`}
          >
            {met ? <Check className="size-3" /> : <X className="size-3" />}
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

function PasswordInput({
  id,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

export const AccountPage = () => {
  const { user, setUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const notify = useNotification();

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [otherNames, setOtherNames] = useState(user?.otherNames ?? '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? '');
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();

  const validateProfile = () => {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!otherNames.trim()) errors.otherNames = 'Other names are required';
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors: Record<string, string> = {};
    if (!currentPassword.trim()) errors.currentPassword = 'Current password is required';
    if (!newPassword.trim()) errors.newPassword = 'New password is required';
    else if (!PASSWORD_RULES.every((r) => r.test(newPassword)))
      errors.newPassword = 'Password does not meet the requirements';
    if (!confirmPassword.trim()) errors.confirmPassword = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (currentPassword && newPassword && currentPassword === newPassword)
      errors.newPassword = 'New password must be different from your current password';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateProfile()) return;

    updateProfileMutation.mutate(
      { firstName, otherNames, phoneNumber: phoneNumber || null },
      {
        onSuccess: (updatedUser) => {
          setUser({ ...user!, ...updatedUser });
          notify.success('Profile updated successfully.');
        },
        onError: (error: any) => {
          const msg = error.response?.data?.message || error.message || 'Failed to update profile';
          notify.error(msg);
          setProfileErrors({ submit: msg });
        },
      }
    );
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;

    changePasswordMutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          notify.success('Password changed. Please log in with your new password.');
          logout();
          navigate('/login');
        },
        onError: (error: any) => {
          const msg = error.response?.data?.message || error.message || 'Failed to change password';
          notify.error(msg);
          setPasswordErrors({ submit: msg });
        },
      }
    );
  };

  if (!user) return null;

  const initials = `${user.firstName[0]}${user.otherNames[0]}`.toUpperCase();

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      {changePasswordMutation.isPending && (
        <LoadingAnimation fullScreen className="z-[100]" text="Changing password and sending confirmation…" />
      )}
      <div>
        <h1 className="text-2xl font-semibold">Account Settings</h1>
        <p className="text-muted-foreground text-sm">Manage your profile and security settings.</p>
      </div>

      <Separator />

      {/* Profile card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Update your personal details.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 flex items-center gap-4">
            <div className="flex size-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-semibold">
              {initials}
            </div>
            <div>
              <p className="font-medium">{user.firstName} {user.otherNames}</p>
              <p className="text-muted-foreground text-sm">{user.email}</p>
            </div>
          </div>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            {profileErrors.submit && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                {profileErrors.submit}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="firstName">
                  First Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (profileErrors.firstName)
                      setProfileErrors((prev) => { const { firstName: _, ...rest } = prev; return rest; });
                  }}
                  disabled={updateProfileMutation.isPending}
                  placeholder="First name"
                />
                {profileErrors.firstName && (
                  <p className="text-xs text-red-500">{profileErrors.firstName}</p>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="otherNames">
                  Other Names <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="otherNames"
                  value={otherNames}
                  onChange={(e) => {
                    setOtherNames(e.target.value);
                    if (profileErrors.otherNames)
                      setProfileErrors((prev) => { const { otherNames: _, ...rest } = prev; return rest; });
                  }}
                  disabled={updateProfileMutation.isPending}
                  placeholder="Other names"
                />
                {profileErrors.otherNames && (
                  <p className="text-xs text-red-500">{profileErrors.otherNames}</p>
                )}
              </Field>
            </div>

            <Field>
              <FieldLabel htmlFor="phoneNumber">Phone Number</FieldLabel>
              <Input
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={updateProfileMutation.isPending}
                placeholder="+254..."
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="username">Username</FieldLabel>
                <Input id="username" value={user.username} disabled className="bg-muted" />
              </Field>
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input id="email" value={user.email} disabled className="bg-muted" />
              </Field>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={updateProfileMutation.isPending}>
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security card */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            After changing your password you will be logged out and must sign in again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {passwordErrors.submit && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                {passwordErrors.submit}
              </div>
            )}

            <Field>
              <FieldLabel htmlFor="currentPassword">
                Current Password <span className="text-red-500">*</span>
              </FieldLabel>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(v) => {
                  setCurrentPassword(v);
                  if (passwordErrors.currentPassword)
                    setPasswordErrors((prev) => { const { currentPassword: _, ...rest } = prev; return rest; });
                }}
                disabled={changePasswordMutation.isPending}
                placeholder="Enter your current password"
              />
              {passwordErrors.currentPassword && (
                <p className="text-xs text-red-500">{passwordErrors.currentPassword}</p>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="newPassword">
                New Password <span className="text-red-500">*</span>
              </FieldLabel>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(v) => {
                  setNewPassword(v);
                  if (passwordErrors.newPassword)
                    setPasswordErrors((prev) => { const { newPassword: _, ...rest } = prev; return rest; });
                }}
                disabled={changePasswordMutation.isPending}
                placeholder="Enter a new password"
              />
              <PasswordRequirements password={newPassword} />
              {passwordErrors.newPassword && (
                <p className="text-xs text-red-500 mt-1">{passwordErrors.newPassword}</p>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="confirmPassword">
                Confirm New Password <span className="text-red-500">*</span>
              </FieldLabel>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(v) => {
                  setConfirmPassword(v);
                  if (passwordErrors.confirmPassword)
                    setPasswordErrors((prev) => { const { confirmPassword: _, ...rest } = prev; return rest; });
                }}
                disabled={changePasswordMutation.isPending}
                placeholder="Confirm your new password"
              />
              {passwordErrors.confirmPassword && (
                <p className="text-xs text-red-500">{passwordErrors.confirmPassword}</p>
              )}
            </Field>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="destructive"
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
